import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  loadUserTariffs,
  LocalizePropType,
  selectTariffs,
  selectUser,
  updateUser,
  withTextLocalizer,
  uploadDocument,
  selectBrandSettings,
  BrandData,
  getNextUserOpenChallengeRoute,
  getAge,
  selectNetworkActivities,
  CLIENT_TYPE,
} from 'getaway-data-layer';
import { useHistory } from 'react-router-dom';
import GrfTariffView from './GrfTariffView';
import { isDashboardScreen, navigateTo, showAlert } from '../../../../../utils';
import { TARIFF_CONSENT_STATUS } from '../../../../../enums';
import { useFilePicker } from 'use-file-picker';

interface Values {}

type Props = {
  localize: LocalizePropType;
};

// Map of discount types to their proof requirements
const discountTypesMapping = {
  'Sparkasse Contomaxx-Mitglied': {
    discount: '15€',
    requiresFile: true,
  },
  'Mitarbeiter Uniklinik Freiburg': {
    discount: '15€',
    requiresFile: true,
  },
  'Digitales Stadt-Starter Paket für Freiburger Neubürger': {
    discount: '25€',
    requiresSubscriptionNumber: true,
  },
  'Familienrabatt bei bereits einer angemeldeten Person im Haushalt': {
    discount: '10€',
    requiresSubscriptionNumber: true,
  },
  'Familienrabatt bei bereits zwei angemeldeten Personen im Haushalt': {
    discount: '15€',
    requiresSubscriptionNumber: true,
  },
  'Familienrabatt bei bereits drei angemeldeten Personen im Haushalt': {
    discount: '20€',
    requiresSubscriptionNumber: true,
  },
  'Mitglieder Bauverein Breisgau eG': {
    discount: '25km',
    requiresFile: true,
  },
  'Mitglieder der Familienheim Freiburg Baugenossenschaft e.G': {
    discount: '25km',
    requiresFile: true,
  },
  'VAG Mitarbeiter': {
    discount: '25km',
    requiresSubscriptionNumber: true,
  },
  'RegioKarten Abo für Erwachsene, Schüler und Azubis': {
    discount: '25km',
    requiresSubscriptionNumber: true,
  },
  'JobTickets BW': {
    discount: '25km',
    requiresSubscriptionNumber: true,
  },
  'Deutschland-Ticket': {
    discount: '25km',
    requiresSubscriptionNumber: true,
  },
  'Semester Ticket Freiburg': {
    discount: '25km',
    requiresFile: true,
  },
  'VRS SemesterTicket': {
    discount: '25km',
    requiresFile: true,
  },
};

// Map for case-insensitive lookups
const discountTypesMappingNormalized = Object.keys(discountTypesMapping).reduce(
  (acc, originalKey) => {
    acc[originalKey.toLowerCase()] = discountTypesMapping[originalKey];
    return acc;
  },
  {}
);

// Helper function to extract discount type from tariff name
const extractDiscountType = (tariffName) => {
  // Extract text in parentheses if it exists
  const match = tariffName.match(/\((.*?)\)/);
  return match ? match[1] : null;
};

// Helper function to get base tariff type (the part before parentheses)
const getBaseTariffType = (tariffName) => {
  const match = tariffName.match(/(.*?)\s*\(/);
  return match ? match[1].trim() : tariffName.trim();
};

const GrfTariffContainer = ({ localize }: Props) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const tariffs = useSelector(selectTariffs);
  const user = useSelector(selectUser);
  const brandSettings = useSelector(selectBrandSettings) as BrandData;
  const networkActivities: any = useSelector(selectNetworkActivities);
  const isLoadingTariffs = networkActivities.some((activityType: any) =>
    [CLIENT_TYPE.USER_CLIENT.LOAD_TARIFFS].includes(activityType)
  );

  const [activeSlide, setActiveSlide] = useState(0);
  const [selectedDiscountType, setSelectedDiscountType] = useState(null);

  const [tariffConsentStatus, setTariffConsentStatus] = useState(
    brandSettings?.tariffConsentRequired !== undefined &&
      brandSettings?.tariffConsentRequired
      ? TARIFF_CONSENT_STATUS.OPEN
      : TARIFF_CONSENT_STATUS.APPROVED
  );
  const [tariffSelected, setTariffSelected] = useState(null);

  // Process tariffs data
  const processedTariffs = React.useMemo(() => {
    if (!tariffs || tariffs.length === 0) {
      return {
        defaultTariffs: [],
        discountTariffs: [],
        discountTypes: [],
        baseTariffTypes: [],
      };
    }

    // Group tariffs based on discount types
    const defaultTariffs = [];
    const discountTariffs = [];
    const discountTypesSet = new Set();
    const baseTariffTypesSet = new Set();

    tariffs.forEach((tariff) => {
      const discountType = extractDiscountType(tariff.name);

      if (!discountType) {
        // This is a base tariff with no discount
        defaultTariffs.push(tariff);
      } else {
        // This is a discount tariff
        const baseType = getBaseTariffType(tariff.name);
        baseTariffTypesSet.add(baseType);
        discountTypesSet.add(discountType);

        // Add discount properties from the mapping
        const discountData =
          discountTypesMappingNormalized[discountType.toLowerCase()];
        if (discountData) {
          discountTariffs.push({
            ...tariff,
            ...discountData,
            discountType,
            baseType,
          });
        }
      }
    });

    return {
      defaultTariffs,
      discountTariffs,
      discountTypes: Array.from(discountTypesSet),
      baseTariffTypes: Array.from(baseTariffTypesSet),
    };
  }, [tariffs]);

  // Group discount tariffs by discount type and base type
  const groupedDiscountTariffs = React.useMemo(() => {
    const result = {};

    processedTariffs.discountTariffs.forEach((tariff) => {
      if (!result[tariff.discountType]) {
        result[tariff.discountType] = {};
      }

      if (!result[tariff.discountType][tariff.baseType]) {
        result[tariff.discountType][tariff.baseType] = [];
      }

      result[tariff.discountType][tariff.baseType].push(tariff);
    });

    return result;
  }, [processedTariffs.discountTariffs]);

  const [subscriptionNum, setSubscriptionNum] = useState('');
  const [subscriptionError, setSubscriptionError] = useState('');

  const [couponNum, setCouponNum] = useState('');

  const [
    openProofOneSelector,
    {
      plainFiles: proofOneFiles,
      loading: proofOneLoading,
      clear: clearProofOne,
    },
  ] = useFilePicker({
    accept: 'image/*',
    multiple: false,
    limitFilesConfig: { max: 1 },
  });
  const [
    openProofTwoSelector,
    {
      plainFiles: proofTwoFiles,
      loading: proofTwoLoading,
      clear: clearProofTwo,
    },
  ] = useFilePicker({
    accept: 'image/*',
    multiple: false,
    limitFilesConfig: { max: 1 },
  });
  const [fileError, setFileError] = useState('');

  const onTariffSelected = (tariff) => {
    setTariffSelected(tariff);
  };

  const onDiscountTypeSelected = (discountType) => {
    setSelectedDiscountType(discountType);
    setTariffSelected(
      processedTariffs?.discountTariffs?.find(
        // @ts-ignore
        (t) => t?.discountType === discountType
      )
    );
    setActiveSlide(2);
  };

  const setDiscountCategorySelected = (tariff) => {
    setTariffSelected(tariff);
  };

  const onSubmit = () => {
    // Mandatory subscriptionNum or Files
    if (
      tariffSelected?.requiresFile &&
      (!proofOneFiles?.[0] || !proofTwoFiles?.[0])
    ) {
      setFileError(localize('required'));
      return;
    }

    if (tariffSelected?.requiresSubscriptionNumber && !subscriptionNum) {
      setSubscriptionError(localize('required'));
      return;
    }

    // if (selectedTlaExtraOption && !plainFiles?.[0]) return;
    if (tariffSelected) {
      dispatch(
        // @ts-ignore
        updateUser(
          {
            ...((subscriptionNum || couponNum) && {
              extraData: JSON.stringify({
                ...(subscriptionNum && { subscriptionNum }),
                ...(couponNum && { couponNum }),
              }),
            }),
            tariff: {
              id: tariffSelected.id,
            },
          },
          (updatedUser) => {
            if (proofOneFiles?.[0]) {
              dispatch(uploadDocument(proofOneFiles?.[0], 'OTHER', () => {}));
            }

            if (proofTwoFiles?.[0]) {
              dispatch(uploadDocument(proofTwoFiles?.[0], 'OTHER', () => {}));
            }

            if (tariffSelected?.upsellings?.length > 0) {
              navigateTo(
                history,
                isDashboardScreen()
                  ? '/dashboard/upsellings'
                  : '/onboarding/upsellings'
              );
            } else {
              navigateTo(history, getNextUserOpenChallengeRoute(updatedUser));
            }
          }
        )
      );
    }
  };

  const onClose = () => {
    if (isDashboardScreen()) {
      navigateTo(history, '/settings');
    } else {
      showAlert(
        '',
        localize('cancel.FirstSteps.dialog.message'),
        localize('cancel.FirstSteps.dialog.yes'),
        () => navigateTo(history, '/settings'),
        localize('cancel.FirstSteps.dialog.no'),
        () => {},
        true
      );
    }
  };

  const onChangeSubscriptionNum = (value) => {
    setSubscriptionNum(value);
    setSubscriptionError('');
  };

  const onChangeCouponNum = (value) => {
    setCouponNum(value);
  };

  const onClickDiscountTariffs = () => {
    setActiveSlide(1);
  };

  const slideBack = () => {
    if (activeSlide === 2) {
      // Go back to discount types
      setActiveSlide(1);
      setSelectedDiscountType(null);
      setSubscriptionNum('');
      clearProofOne();
      clearProofTwo();
      setCouponNum('');
    } else {
      // Go back to default tariffs
      setActiveSlide(activeSlide - 1);
      setTariffSelected(processedTariffs.defaultTariffs?.[0]);
    }
  };

  useEffect(() => {
    setFileError('');
  }, [proofOneFiles?.[0]]);

  useEffect(() => {
    setFileError('');
  }, [proofTwoFiles?.[0]]);

  useEffect(() => {
    dispatch(loadUserTariffs());
  }, []);

  useEffect(() => {
    if (!isLoadingTariffs && tariffs !== null && tariffs?.length === 0) {
      if (user?.dateOfBirth && brandSettings?.kycSettings?.minimalUserAge) {
        const userAge = getAge(user.dateOfBirth);
        const minimalUserAge = brandSettings.kycSettings.minimalUserAge;

        if (userAge < minimalUserAge) {
          // user is underage
          navigateTo(history, '/settings');
        }
      }
    }
  }, [
    isLoadingTariffs,
    tariffs,
    user?.dateOfBirth,
    brandSettings?.kycSettings?.minimalUserAge,
  ]);

  useEffect(() => {
    if (!user?.tariff?.id) {
      if (processedTariffs.defaultTariffs?.length > 0) {
        setTariffSelected(processedTariffs.defaultTariffs[0]);
      }
    } else if (tariffs && user?.tariff?.id) {
      setTariffSelected(
        tariffs?.find((tariff) => tariff?.id === user?.tariff?.id)
      );
    }
  }, [processedTariffs.defaultTariffs, user]);

  return (
    <GrfTariffView
      defaultTariffs={processedTariffs.defaultTariffs}
      discountTariffs={processedTariffs.discountTariffs}
      discountTypes={processedTariffs.discountTypes}
      baseTariffTypes={processedTariffs.baseTariffTypes}
      selectedDiscountType={selectedDiscountType}
      groupedDiscountTariffs={groupedDiscountTariffs}
      isOrg={user?.userType === 'org'}
      localize={localize}
      onClose={onClose}
      tariffSelected={tariffSelected}
      setTariffSelected={onTariffSelected}
      tariffConsentStatus={tariffConsentStatus}
      setTariffConsentStatus={setTariffConsentStatus}
      onSubmit={onSubmit}
      openProofOneSelector={openProofOneSelector}
      openProofTwoSelector={openProofTwoSelector}
      proofOneLoading={proofOneLoading}
      proofTwoLoading={proofTwoLoading}
      proofOne={proofOneFiles?.[0]}
      proofTwo={proofTwoFiles?.[0]}
      clearProofOne={clearProofOne}
      clearProofTwo={clearProofTwo}
      subscriptionNum={subscriptionNum}
      setSubscriptionNum={onChangeSubscriptionNum}
      subscriptionError={subscriptionError}
      fileError={fileError}
      tariffConsentRequired={brandSettings?.tariffConsentRequired}
      activeSlide={activeSlide}
      onClickDiscountTariffs={onClickDiscountTariffs}
      slideBack={slideBack}
      onDiscountTypeSelected={onDiscountTypeSelected}
      setDiscountCategorySelected={setDiscountCategorySelected}
      couponNum={couponNum}
      setCouponNum={onChangeCouponNum}
    />
  );
};

export default withTextLocalizer(GrfTariffContainer);
